import {
  BookOpen,
  Building2,
  Camera,
  Coffee,
  Dices,
  Flag,
  Folder,
  Footprints,
  Glasses,
  Handshake,
  Hourglass,
  Image,
  Laptop,
  Lightbulb,
  Map,
  Mic,
  Music,
  Paintbrush,
  Palette,
  Palmtree,
  PartyPopper,
  Plane,
  Scissors,
  Shirt,
  Sparkles,
  Truck,
  Users,
  Utensils,
  Video,
  Wrench
} from 'lucide-react';
import { ReactNode } from 'react';

export type ColorPickerLibraryItem = {
  id: string;
  name: string;
  /** Value emitted on select (e.g. hex color). */
  value: string;
  /** Optional custom swatch; defaults to a filled circle using `value`. */
  render?: ReactNode;
};

export type IconPickerLibraryItem = {
  id: string;
  name: string;
  render: ReactNode;
};

export type ColorPickerLibrary = {
  colors: readonly ColorPickerLibraryItem[];
};

export type IconPickerLibrary = {
  icons: readonly IconPickerLibraryItem[];
};

export type ColorIconPickerLibrary = ColorPickerLibrary & IconPickerLibrary;

const iconClassName = 'size-4 shrink-0';

function icon(
  id: string,
  name: string,
  render: ReactNode
): IconPickerLibraryItem {
  return { id, name, render };
}

export const defaultColorLibrary: ColorPickerLibrary = {
  colors: [
    { id: 'pink', name: 'Pink', value: '#F9A8D4' },
    { id: 'purple', name: 'Purple', value: '#C4B5FD' },
    { id: 'peach', name: 'Peach', value: '#FED7AA' },
    { id: 'lime', name: 'Lime', value: '#BBF7D0' },
    { id: 'olive', name: 'Olive', value: '#D9F99D' },
    { id: 'yellow', name: 'Yellow', value: '#FDE047' },
    { id: 'mint', name: 'Mint', value: '#99F6E4' },
    { id: 'seafoam', name: 'Seafoam', value: '#A5F3FC' }
  ]
};

export const defaultIconLibrary: IconPickerLibrary = {
  icons: [
    icon(
      'folder',
      'Folder',
      <Folder className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'users',
      'Users',
      <Users className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'building',
      'Building',
      <Building2 className={iconClassName} strokeWidth={1.5} />
    ),
    icon('flag', 'Flag', <Flag className={iconClassName} strokeWidth={1.5} />),
    icon('map', 'Map', <Map className={iconClassName} strokeWidth={1.5} />),
    icon(
      'book',
      'Book',
      <BookOpen className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'video',
      'Video',
      <Video className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'laptop',
      'Laptop',
      <Laptop className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'mic',
      'Microphone',
      <Mic className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'music',
      'Music',
      <Music className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'camera',
      'Camera',
      <Camera className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'lightbulb',
      'Lightbulb',
      <Lightbulb className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'party',
      'Party',
      <PartyPopper className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'coffee',
      'Coffee',
      <Coffee className={iconClassName} strokeWidth={1.5} />
    ),
    icon('dice', 'Dice', <Dices className={iconClassName} strokeWidth={1.5} />),
    icon(
      'cutlery',
      'Cutlery',
      <Utensils className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'palm',
      'Palm tree',
      <Palmtree className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'palette',
      'Palette',
      <Palette className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'glasses',
      'Glasses',
      <Glasses className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'handshake',
      'Handshake',
      <Handshake className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'hourglass',
      'Hourglass',
      <Hourglass className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'sparkles',
      'Sparkles',
      <Sparkles className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'paintbrush',
      'Paintbrush',
      <Paintbrush className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'photo',
      'Photo',
      <Image className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'scissors',
      'Scissors',
      <Scissors className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'footprints',
      'Footprints',
      <Footprints className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'plane',
      'Plane',
      <Plane className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'tools',
      'Tools',
      <Wrench className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'truck',
      'Truck',
      <Truck className={iconClassName} strokeWidth={1.5} />
    ),
    icon(
      'shirt',
      'Shirt',
      <Shirt className={iconClassName} strokeWidth={1.5} />
    )
  ]
};

export const defaultColorIconLibrary: ColorIconPickerLibrary = {
  ...defaultColorLibrary,
  ...defaultIconLibrary
};

export function findColorLibraryItem(
  library: ColorPickerLibrary,
  value: string
): ColorPickerLibraryItem | undefined {
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  return library.colors.find(
    item => item.value === normalized || item.id === normalized
  );
}

export function findIconLibraryItem(
  library: IconPickerLibrary,
  iconId: string
): IconPickerLibraryItem | undefined {
  const normalized = iconId.trim();
  if (!normalized) {
    return undefined;
  }
  return library.icons.find(item => item.id === normalized);
}
